import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import Seo, { breadcrumbLd, faqLd, SITE_URL } from "@/components/Seo";
import { MarketingNav, MarketingFooter, MarketingCTA } from "@/components/MarketingChrome";
import { getSolution, SOLUTIONS } from "@/data/seoContent";

export default function SolutionsPage() {
  const { slug } = useParams();
  const s = getSolution(slug);
  if (!s) return <Navigate to="/solutions" replace />;

  const jsonLd = [
    breadcrumbLd([
      { name: "Home", path: "/" },
      { name: "Solutions", path: "/solutions" },
      { name: s.keyword, path: `/solutions/${s.slug}` },
    ]),
    faqLd(s.faqs),
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Ask Redline",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web, iOS, Android (SMS/voice, no app required)",
      description: s.metaDescription,
      publisher: { "@type": "Organization", name: "Ask Redline", url: SITE_URL },
    },
  ];

  const related = (s.related || []).map(getSolution).filter(Boolean);

  return (
    <div className="font-body bg-sand-50 text-ink min-h-screen">
      <Seo title={s.metaTitle} description={s.metaDescription} canonical={`/solutions/${s.slug}`} jsonLd={jsonLd} scrollTop />
      <MarketingNav />

      {/* HERO */}
      <section className="border-b border-sand-200">
        <div className="max-w-6xl mx-auto px-6 pt-14 pb-16">
          <Link to="/solutions" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-500 hover:text-redline-600 transition-colors mb-6" data-testid="solution-back">
            <ArrowLeft className="h-4 w-4" /> All solutions
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-redline-50 text-redline-700 text-[11px] font-bold uppercase tracking-[0.16em] px-3 py-1.5 mb-5">{s.hero.eyebrow}</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.08]" data-testid="solution-title">{s.hero.title}</h1>
            <p className="mt-5 text-lg text-ink-700 leading-relaxed">{s.hero.sub}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="/#contact" className="inline-flex items-center gap-2 rounded-full bg-redline-500 text-white font-semibold px-6 py-3 hover:bg-redline-600 transition-colors">Book a demo <ArrowRight className="h-4 w-4" /></a>
              <Link to="/compare" className="inline-flex items-center gap-2 rounded-full border border-sand-200 bg-white font-semibold px-6 py-3 hover:border-ink transition-colors">Compare options</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-3xl mx-auto px-6 py-14">
        {s.intro.map((p, i) => (
          <p key={i} className="text-lg text-ink-700 leading-relaxed mb-5 last:mb-0">{p}</p>
        ))}
      </section>

      {/* FEATURES */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {s.features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
              className="rounded-3xl border border-sand-200 bg-white p-7 hover:-translate-y-1 hover:shadow-[0_20px_50px_-24px_rgba(15,15,20,0.25)] transition-all">
              <div className="h-11 w-11 rounded-xl bg-redline-50 flex items-center justify-center mb-4"><f.icon className="h-5 w-5 text-redline-600" strokeWidth={1.75} /></div>
              <h3 className="font-heading text-lg font-extrabold tracking-tight mb-2">{f.title}</h3>
              <p className="text-ink-700 text-sm leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="relative overflow-hidden rounded-3xl bg-ink text-white p-8 sm:p-12">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-redline-500/20 blur-3xl" />
          <div className="relative">
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mb-6">What you get out of it</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {s.outcomes.map((o) => (
                <div key={o} className="flex items-start gap-3 text-sand-100"><Check className="h-5 w-5 text-redline-400 shrink-0 mt-0.5" strokeWidth={2.5} /> {o}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mb-6">Frequently asked</h2>
        <div className="space-y-3">
          {s.faqs.map((f, i) => (
            <div key={i} className="rounded-2xl border border-sand-200 bg-white p-5" data-testid={`solution-faq-${i}`}>
              <div className="font-heading font-bold text-ink">{f.q}</div>
              <p className="text-ink-700 text-sm mt-2 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 pb-20">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-ink-400 mb-4">Related solutions</div>
          <div className="grid sm:grid-cols-3 gap-4">
            {related.map((r) => (
              <Link key={r.slug} to={`/solutions/${r.slug}`} className="group rounded-2xl border border-sand-200 bg-white p-5 hover:border-redline-300 transition-colors" data-testid={`solution-related-${r.slug}`}>
                <div className="font-heading font-bold text-ink group-hover:text-redline-600 transition-colors">{r.hero.eyebrow}</div>
                <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-redline-600">Learn more <ArrowRight className="h-4 w-4" /></span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <MarketingCTA />
      <MarketingFooter />
    </div>
  );
}
